import React from 'react';

// Doors share one glyph across all 5 door sub-types (front/rear/kitchen/toilet/store room) —
// distinguished by their label text and canvas position, not by icon shape. Drawn as a closed
// door slab with a handle, not an open-door swing diagram — simpler to read at small canvas sizes.
const DoorIcon = () => (
  <svg viewBox="0 0 24 24" width="100%" height="100%">
    <rect x="5" y="2" width="14" height="20" rx="1" fill="none" stroke="currentColor" strokeWidth="1.5" />
    <circle cx="16" cy="12" r="1.1" fill="currentColor" />
  </svg>
);

const ToiletIcon = () => (
  <svg viewBox="0 0 24 24" width="100%" height="100%">
    <rect x="6" y="3" width="12" height="6" rx="1.5" fill="none" stroke="currentColor" strokeWidth="1.5" />
    <path
      d="M7 9v6a5 5 0 0 0 10 0V9"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
    />
    <path d="M8 21h8l-1.2-3H9.2z" fill="currentColor" />
  </svg>
);

// Shared by every other room sub-type (reception/lobby/party hall/DJ/bar/lounge/kitchen/store) —
// same "one glyph, label does the work" convention as doors.
const RoomIcon = () => (
  <svg viewBox="0 0 24 24" width="100%" height="100%">
    <rect x="3" y="3" width="18" height="18" rx="1" fill="currentColor" opacity="0.12" />
    <rect x="3" y="3" width="18" height="18" rx="1" fill="none" stroke="currentColor" strokeWidth="1.5" />
    <circle cx="7.5" cy="7.5" r="1" fill="currentColor" />
  </svg>
);

const PlantIcon = () => (
  <svg viewBox="0 0 24 24" width="100%" height="100%">
    <path d="M12 21V11" fill="none" stroke="currentColor" strokeWidth="1.5" />
    <path
      d="M12 11c0-4 -3-6-7-6 0 4 3 6 7 6Zm0 0c0-4 3-6 7-6 0 4 -3 6-7 6Z"
      fill="currentColor"
      opacity="0.85"
    />
    <path d="M7 21h10l-1-4H8Z" fill="currentColor" />
  </svg>
);

const StatueIcon = () => (
  <svg viewBox="0 0 24 24" width="100%" height="100%">
    <rect x="7" y="17" width="10" height="4" fill="currentColor" />
    <path d="M9 17c0-4 1-6 1-9a2 2 0 1 1 4 0c0 3 1 5 1 9Z" fill="currentColor" opacity="0.9" />
    <circle cx="12" cy="6" r="2.2" fill="currentColor" />
  </svg>
);

const VaseIcon = () => (
  <svg viewBox="0 0 24 24" width="100%" height="100%">
    <path
      d="M10 3h4l0.6 2.5c1.6 1 2.4 3 2.1 5.2l-0.7 5.3c-0.3 2.3-2.2 4-4.5 4h-1c-2.3 0-4.2-1.7-4.5-4l-0.7-5.3c-0.3-2.2 0.5-4.2 2.1-5.2Z"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.4"
    />
    <path d="M9.6 5.5h4.8" stroke="currentColor" strokeWidth="1.4" />
  </svg>
);

const VendingMachineIcon = () => (
  <svg viewBox="0 0 24 24" width="100%" height="100%">
    <rect x="4" y="2" width="16" height="20" rx="1" fill="none" stroke="currentColor" strokeWidth="1.4" />
    <rect x="6" y="4" width="12" height="9" fill="none" stroke="currentColor" strokeWidth="1" />
    <line x1="10" y1="4" x2="10" y2="13" stroke="currentColor" strokeWidth="0.8" />
    <line x1="14" y1="4" x2="14" y2="13" stroke="currentColor" strokeWidth="0.8" />
    <line x1="6" y1="8.5" x2="18" y2="8.5" stroke="currentColor" strokeWidth="0.8" />
    <rect x="6" y="16" width="6" height="4" fill="none" stroke="currentColor" strokeWidth="1" />
    <circle cx="15.5" cy="18" r="1" fill="currentColor" />
  </svg>
);

const IceCreamDispenserIcon = () => (
  <svg viewBox="0 0 24 24" width="100%" height="100%">
    <path d="M9 12l3 9 3-9Z" fill="currentColor" opacity="0.85" />
    <path
      d="M7 11c0-2.8 2.2-5 5-5s5 2.2 5 5c0 1.6-1.3 2.5-2.6 1.8-0.9-0.5-1.6-0.1-2.4 0.4-0.8 0.5-1.6 0.9-2.4 0.4C8.3 13.5 7 12.6 7 11Z"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.3"
    />
  </svg>
);

const FallbackIcon = () => (
  <svg viewBox="0 0 24 24" width="100%" height="100%">
    <rect x="3" y="3" width="18" height="18" rx="3" fill="none" stroke="currentColor" strokeWidth="1.5" />
  </svg>
);

const ICONS_BY_TYPE = {
  front_door: DoorIcon,
  rear_door: DoorIcon,
  kitchen_door: DoorIcon,
  toilet_door: DoorIcon,
  store_room_door: DoorIcon,

  reception_room: RoomIcon,
  lobby_room: RoomIcon,
  party_hall_room: RoomIcon,
  dj_room: RoomIcon,
  bar_room: RoomIcon,
  lounge_room: RoomIcon,
  toilet_room: ToiletIcon,
  kitchen_room: RoomIcon,
  store_room: RoomIcon,

  plant: PlantIcon,
  statue: StatueIcon,
  vase: VaseIcon,

  vending_machine: VendingMachineIcon,
  ice_cream_dispenser: IceCreamDispenserIcon,
};

const LayoutElementIcon = ({ type }) => {
  const Icon = ICONS_BY_TYPE[type] || FallbackIcon;
  return <Icon />;
};

export default LayoutElementIcon;
